import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment-timezone';
import { isAbsoluteEmpty } from 'ziphy-web-shared/basic/helpers';
import { ConnectedGroup } from 'ziphy-web-shared/basic/lib/simple';
import { decomposeDateStringToDateComponents } from 'ziphy-web-shared/basic/utils/time';
import { Select } from '../';
import { getDates, getDayWithinDaysRange, getMonthWithinMonthsRange } from './DateSelect.helpers';
import styles from './DateSelect.module.scss';
import useOnChange from './useOnChange';
import useStates from './useStates';
const DateSelect = ({ value, onChange, isInvalid, isFull = false, 
//
label, mode, disabled, readonly, 
//
minDate = -36500, maxDate = 36500, alwaysReturnAsString = true, className, }) => {
    const { t } = useTranslation();
    const memoizedDates = useMemo(() => {
        const tempDefaultDate = !_.isEmpty(value) ? new Date(value) : new Date();
        const tempDefaultDateComponents = !_.isEmpty(value)
            ? decomposeDateStringToDateComponents(value)
            : null;
        const parseDate = (date) => {
            if (date === 'today')
                return moment();
            if (typeof date === 'number') {
                return date >= 0 ? moment().add(date, 'days') : moment().subtract(Math.abs(date), 'days');
            }
            return moment(date);
        };
        const min = parseDate(minDate).toDate();
        const max = parseDate(maxDate).toDate();
        return { default: tempDefaultDate, defaultDateComponents: tempDefaultDateComponents, min, max };
    }, [value, maxDate, minDate]);
    const { day, month, year, setYear, setMonth, setDay } = useStates({
        minDate: memoizedDates.min,
        maxDate: memoizedDates.max,
        defaultDate: memoizedDates.default,
        defaultDateComponents: memoizedDates.defaultDateComponents,
        isFull,
    });
    const invalidFields = useOnChange({
        fields: { month, day, year },
        onChange,
        isFull,
        alwaysReturnAsString,
    });
    const { years, defaultMonths } = useMemo(() => getDates(memoizedDates.min, memoizedDates.max), [memoizedDates]);
    const [firstYear, lastYear] = useMemo(() => [_.first(years), _.last(years)], [years]);
    const months = useMemo(() => {
        let newMonths;
        if (year === firstYear.id)
            newMonths = firstYear.months;
        else if (year === lastYear.id)
            newMonths = lastYear.months;
        else
            newMonths = defaultMonths;
        const newMonth = getMonthWithinMonthsRange(month, newMonths);
        if (newMonth !== month)
            setMonth(newMonth);
        return newMonths;
    }, [years, year]);
    const days = useMemo(() => {
        var _a, _b;
        if (months.length === 12)
            return ((_a = months[month]) === null || _a === void 0 ? void 0 : _a.days) || [];
        return ((_b = months.find(({ id }) => id === month)) === null || _b === void 0 ? void 0 : _b.days) || [];
    }, [months, month]);
    useEffect(() => {
        const newDay = getDayWithinDaysRange(day, days);
        if (newDay !== day)
            setDay(newDay);
    }, [days]);
    return (_jsxs("div", Object.assign({ className: classNames(styles.dateSelect, className) }, { children: [typeof label === 'string' && _jsx("div", Object.assign({ className: styles.label }, { children: label })), _jsx(ConnectedGroup, { wrap: true, renderItems: [
                    ({ getClass }) => (_jsx(Select, { items: months, field: {
                            value: getMonthWithinMonthsRange(month, months),
                            isInvalid: isInvalid || (invalidFields === null || invalidFields === void 0 ? void 0 : invalidFields.month),
                            onChange: (ids) => {
                                const newMonth = ids;
                                if (isAbsoluteEmpty(newMonth)) {
                                    setDay(undefined);
                                }
                                setMonth(newMonth);
                            },
                        }, mode: mode, label: label === true ? t('label.month') : undefined, placeholder: label === true ? t(isFull ? 'label.required' : 'label.optional') : t('label.month'), allowUnselect: !isFull, innerClassName: getClass(), className: styles.select, readonly: readonly, disabled: disabled })),
                    ({ getClass }) => (_jsx(Select, { items: days, field: {
                            value: getDayWithinDaysRange(day, days),
                            isInvalid: isInvalid || (invalidFields === null || invalidFields === void 0 ? void 0 : invalidFields.day),
                            onChange: (id) => setDay(id),
                        }, mode: mode, label: label === true ? t('label.day') : undefined, placeholder: label === true ? t(isFull ? 'label.required' : 'label.optional') : t('label.day'), allowUnselect: !isFull, innerClassName: getClass(), className: styles.select, readonly: readonly, disabled: !_.isNumber(month) || disabled })),
                    ({ getClass }) => (_jsx(Select, { items: years, field: {
                            value: year,
                            isInvalid: isInvalid || (invalidFields === null || invalidFields === void 0 ? void 0 : invalidFields.year),
                            onChange: (ids) => setYear(ids),
                        }, mode: mode, label: label === true ? t('label.year') : undefined, placeholder: label === true ? t(isFull ? 'label.required' : 'label.optional') : t('label.year'), innerClassName: getClass(), className: styles.select, readonly: readonly, disabled: disabled })),
                ] })] })));
};
export default memo(DateSelect);
